import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
const API_BASE = "https://demo.zorbastays.com/web-backend/billing";

// Formats "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") as "DD-MM-YYYY".
// String-based rather than `new Date(...)` + local getters, so it can't be
// shifted by a day depending on the browser's timezone.
const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
};

// invoiceid: the cashback invoice to load and display (falls back to the
// :invoiceid route param so this works whether passed as a prop or rendered
// straight from a route)
const CashbackView = ({ invoiceid: invoiceidProp }) => {
  const navigate = useNavigate();

  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_BASE}/cashback-invoice/${invoiceid}`);
        const result = await res.json();

        if (!res.ok || !result.success) {
          setLoadError(result.message || "Failed to load cashback invoice.");
          return;
        }

        const inv = result.invoice;
        setData({
          invoiceno: inv.invoiceno,
          invoicedate: inv.invoicedate,
          guestname: inv.guestname,
          rows: inv.items.map((item) => ({
            particulars: item.particulars,
            from: item.checkin,
            to: item.checkout,
            nights: item.nights,
            amount: item.amount,
            cashback: item.cashback,
          })),
          amountinwords: inv.amountinwords,
          totalAmount: inv.total,
        });
      } catch (err) {
        console.error("Error loading cashback invoice:", err);
        setLoadError(
          "Something went wrong while loading the cashback invoice.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        Loading cashback invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        No cashback invoice to display.
      </div>
    );
  }

  const {
    invoiceno,
    invoicedate,
    guestname,
    rows,
    amountinwords,
    totalAmount,
  } = data;

  return (
    <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
      <style>{`
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Cashback Invoice</h1>
          <br />
          <br />
          <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
          <p className="text-sm">
            306, 7 - Business Centrum <br /> Amin Marg, Rajkot, 360001
          </p>
          <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p>
        </div>
        <div className="w-24 h-24">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div className="border-t border-black mb-6"></div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mb-8">
        <div>
          <p className="text-xs font-bold uppercase">Invoice No.</p>
          <p className="font-medium">{invoiceno}</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase">Invoice Date</p>
          <p className="font-medium">{formatDate(invoicedate)}</p>
        </div>
      </div>

      <div className="mb-8">
        <p className="font-bold">Invoice To:-</p>
        <p className="text-lg">{guestname}</p>
      </div>

      {/* Table Design */}
      <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
        <thead>
          <tr className="uppercase text-xs font-bold border-b border-black">
            <th className="py-3 border-black w-[60px]">Sr. No.</th>
            <th className="py-3">Particulars</th>
            <th className="py-3">Period of stay</th>
            <th className="py-3">No. Nights</th>
            <th className="py-3">Bill Amount</th>
            <th className="py-3">Cashback</th>
          </tr>
        </thead>
        <tbody>
          {rows?.map((row, index) => (
            <tr key={index} className="border-b border-black">
              <td className="py-3  border-black">{index + 1}</td>
              <td className="py-3 px-2">{row.particulars}</td>
              <td className="py-3 px-2">
                {row.from && row.to
                  ? `${formatDate(row.from)} to ${formatDate(row.to)}`
                  : "-"}
              </td>
              <td className="py-3">{row.nights}</td>
              <td className="py-3">{row.amount}</td>
              <td className="py-3">{row.cashback}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-between items-start mt-6 px-2">
        <div className="w-1/2">
          <p className="font-bold mb-1">In Words:</p>
          <p className="uppercase border-b border-black">{amountinwords}</p>
        </div>
        <div className="w-48">
          <div className="flex justify-between py-1 font-bold border-b border-black">
            <span>Total</span>
            <span>{totalAmount}</span>
          </div>
        </div>
      </div>

      <div className="mt-20 text-center text-xs border-t border-black pt-8">
        <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
        <button
          onClick={() => window.print()}
          className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Print Invoice
        </button>
        <button
          onClick={() => navigate("/bills")}
          className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all duration-300 text-sm"
        >
          Back to Bills
        </button>
      </div>
    </div>
  );
};

export default CashbackView;
